import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { useLazyGetPdfJobQuery } from "features/pdfTools/pdfTools";
import { removePdfSplitJob } from "features/pdfSplitJobsSlice";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch, useAppSelector } from "features/store";
import type { RootState } from "features/store";

const POLL_MS = 4000;

export default function PdfSplitJobPoller() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const [fetchJob] = useLazyGetPdfJobQuery();

  const jobCount = useAppSelector(
    (s) => Object.keys(s.pdfSplitJobs.jobs).length,
  );

  useEffect(() => {
    if (jobCount === 0) return;
    let stopped = false;
    let tickInFlight = false;

    const runTick = async () => {
      if (stopped || tickInFlight) return;
      tickInFlight = true;
      try {
        const jobs = store.getState().pdfSplitJobs.jobs;
        const jobIds = Object.keys(jobs);
        if (jobIds.length === 0) return;

        for (const jobId of jobIds) {
          const label = jobs[jobId]?.label ?? "PDF split";
          try {
            const job = await fetchJob(jobId).unwrap();
            if (job.status === "completed") {
              dispatchRef.current(removePdfSplitJob({ jobId }));
              const errorPages = job.summary?.totals?.error_pages ?? 0;
              dispatchRef.current(
                pushNotification({
                  id: `pdf-split-done-${jobId}`,
                  message:
                    errorPages > 0
                      ? `Split job "${label}" finished with ${errorPages} error page(s). Review the results.`
                      : `Split job "${label}" finished. Review the results.`,
                  severity: errorPages > 0 ? "warning" : "success",
                  autoHideMs: null,
                  downloadAction: {
                    kind: "pdf_split_view",
                    jobId,
                    label,
                  },
                }),
              );
            } else if (job.status === "failed") {
              dispatchRef.current(removePdfSplitJob({ jobId }));
              dispatchRef.current(
                pushNotification({
                  message:
                    job.error_message?.trim() ||
                    `Split job "${label}" failed.`,
                  severity: "error",
                }),
              );
            } else if (job.status === "stopped" || job.status === "canceled") {
              dispatchRef.current(removePdfSplitJob({ jobId }));
              dispatchRef.current(
                pushNotification({
                  message: `Split job "${label}" was stopped. You can resume it or start over.`,
                  severity: "info",
                }),
              );
            }
          } catch (err: unknown) {
            const status = (err as { status?: number })?.status;
            if (status === 404) {
              dispatchRef.current(removePdfSplitJob({ jobId }));
            }
          }
        }
      } finally {
        tickInFlight = false;
      }
    };

    void runTick();
    const interval = setInterval(() => void runTick(), POLL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [jobCount, store, fetchJob]);

  return null;
}
